import React, { useState } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler, PlayerHeaderInfo } from '../PlayerHandler';

interface PlayerActionsProps {
  header: PlayerHeaderInfo;
  onDataChanged: () => void;
  onAdjustRating: () => void;
}

// UserStatus values matching backend string enum serialization
const allStatuses = ['Disabled', 'Active', 'Locked', 'UnderReview', 'Flagged'] as const;

const statusLabels: Record<string, string> = {
  Disabled: 'Disabled',
  Active: 'Active',
  Locked: 'Locked',
  UnderReview: 'Under Review',
  Flagged: 'Flagged',
};

const statusColors: Record<string, { bg: string; color: string }> = {
  Active: { bg: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' },
  Disabled: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' },
  Locked: { bg: 'rgba(234, 179, 8, 0.15)', color: '#eab308' },
  UnderReview: { bg: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' },
  Flagged: { bg: 'rgba(249, 115, 22, 0.15)', color: '#f97316' },
};

const defaultStatusColor = { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280' };

const PlayerActions: React.FC<PlayerActionsProps> = ({ header, onDataChanged, onAdjustRating }) => {
  const { theme, isMobile } = useTheme();
  const [showConfirm, setShowConfirm] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [showUpdateItems, setShowUpdateItems] = useState(false);
  const [itemsReview, setItemsReview] = useState(false);
  const [itemsBoards, setItemsBoards] = useState(0);
  const [itemsHints, setItemsHints] = useState(0);
  const [itemsMoreTime, setItemsMoreTime] = useState(0);
  const [itemsReason, setItemsReason] = useState('');

  const currentStatus = String(header.userStatus ?? 'Active');
  const currentStatusColor = statusColors[currentStatus] ?? defaultStatusColor;

  // The Update Items inputs hold the player's NEW target balances (pre-filled with
  // their current values); the delta we send to the backend is new - current.
  const currentBoards = header.boards ?? 0;
  const currentHints = header.hints ?? 0;
  const currentMoreTime = header.moreTime ?? 0;
  const deltaBoards = itemsBoards - currentBoards;
  const deltaHints = itemsHints - currentHints;
  const deltaMoreTime = itemsMoreTime - currentMoreTime;
  const hasItemChanges = deltaBoards !== 0 || deltaHints !== 0 || deltaMoreTime !== 0;

  const handleStatusChange = async (newStatus: string) => {
    if (!header.userId) return;
    setLoading(true);
    try {
      const handler = new PlayerHandler();
      await handler.updatePlayerStatus(header.userId, newStatus, reason);
      setShowConfirm(null);
      setReason('');
      onDataChanged();
    } catch (err) {
      console.error('Error updating player status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdFreeToggle = async () => {
    if (!header.userId) return;
    setLoading(true);
    try {
      const handler = new PlayerHandler();
      await handler.togglePlayerAdFree(header.userId, !header.noAds);
      onDataChanged();
    } catch (err) {
      console.error('Error toggling ad-free:', err);
    } finally {
      setLoading(false);
    }
  };

  const openUpdateItems = () => {
    setItemsBoards(currentBoards);
    setItemsHints(currentHints);
    setItemsMoreTime(currentMoreTime);
    setItemsReason('');
    setItemsReview(false);
    setShowUpdateItems(true);
  };

  const closeUpdateItems = () => {
    setShowUpdateItems(false);
    setItemsReview(false);
    setItemsReason('');
  };

  const handleUpdateItems = async () => {
    if (!header.userId || !hasItemChanges || !itemsReason.trim()) return;
    setLoading(true);
    try {
      const handler = new PlayerHandler();
      await handler.updatePlayerItems(header.userId, deltaBoards, deltaHints, deltaMoreTime, itemsReason.trim());
      closeUpdateItems();
      onDataChanged();
    } catch (err) {
      console.error('Error updating items:', err);
    } finally {
      setLoading(false);
    }
  };

  // Status actions available based on current status
  const statusActions = allStatuses
    .filter((s) => s !== currentStatus)
    .map((s) => ({ label: s, value: s }));

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '10px',
      padding: isMobile ? '12px' : '16px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap' as const,
      gap: '12px',
    },
    title: {
      fontSize: '14px',
      fontWeight: 600,
      color: theme.text,
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 12px',
      borderRadius: '14px',
      background: currentStatusColor.bg,
      color: currentStatusColor.color,
      fontWeight: 600,
      fontSize: '12px',
    },
    actionsRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap' as const,
    },
    actionBtn: {
      padding: '6px 14px',
      fontSize: '12px',
      fontWeight: 500,
      border: `1px solid ${theme.border}`,
      borderRadius: '6px',
      cursor: 'pointer',
      background: theme.card,
      color: theme.text,
      transition: 'all 0.2s',
    },
    dangerBtn: {
      padding: '6px 14px',
      fontSize: '12px',
      fontWeight: 500,
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '6px',
      cursor: 'pointer',
      background: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444',
      transition: 'all 0.2s',
    },
    successBtn: {
      padding: '6px 14px',
      fontSize: '12px',
      fontWeight: 500,
      border: '1px solid rgba(34, 197, 94, 0.3)',
      borderRadius: '6px',
      cursor: 'pointer',
      background: 'rgba(34, 197, 94, 0.1)',
      color: '#22c55e',
      transition: 'all 0.2s',
    },
    ratingBtn: {
      padding: '6px 14px',
      fontSize: '12px',
      fontWeight: 500,
      border: '1px solid rgba(168, 85, 247, 0.3)',
      borderRadius: '6px',
      cursor: 'pointer',
      background: 'rgba(168, 85, 247, 0.1)',
      color: '#a855f7',
      transition: 'all 0.2s',
    },
    grantBtn: {
      padding: '6px 14px',
      fontSize: '12px',
      fontWeight: 500,
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '6px',
      cursor: 'pointer',
      background: 'rgba(59, 130, 246, 0.1)',
      color: '#3b82f6',
      transition: 'all 0.2s',
    },
    numberInput: {
      width: '80px',
      padding: '8px 12px',
      fontSize: '13px',
      border: `1px solid ${theme.border}`,
      borderRadius: '6px',
      background: theme.inputBg,
      color: theme.text,
      outline: 'none',
      boxSizing: 'border-box' as const,
    },
    grantFieldRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '8px',
      flexWrap: 'wrap' as const,
    },
    fieldLabel: {
      fontSize: '12px',
      fontWeight: 500,
      color: theme.textSecondary,
      minWidth: '50px',
    },
    hintText: {
      fontSize: '11px',
      color: theme.textSecondary,
      fontStyle: 'italic' as const,
    },
    summaryRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      fontSize: '13px',
      color: theme.text,
      padding: '4px 0',
    },
    summaryLabel: {
      fontSize: '13px',
      color: theme.textSecondary,
    },
    summaryValue: {
      fontSize: '13px',
      color: theme.text,
      fontWeight: 500,
    },
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    toggleLabel: {
      fontSize: '12px',
      color: theme.textSecondary,
    },
    toggle: {
      width: '36px',
      height: '20px',
      borderRadius: '10px',
      background: header.noAds ? '#22c55e' : theme.border,
      position: 'relative' as const,
      cursor: 'pointer',
      transition: 'background 0.2s',
    },
    toggleDot: {
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      background: '#fff',
      position: 'absolute' as const,
      top: '2px',
      left: header.noAds ? '18px' : '2px',
      transition: 'left 0.2s',
    },
    confirmOverlay: {
      marginTop: '12px',
      padding: '12px',
      background: theme.inputBg,
      borderRadius: '8px',
      border: `1px solid ${theme.border}`,
    },
    confirmTitle: {
      fontSize: '13px',
      fontWeight: 600,
      color: theme.text,
      marginBottom: '8px',
    },
    reasonInput: {
      width: '100%',
      padding: '8px 12px',
      fontSize: '13px',
      border: `1px solid ${theme.border}`,
      borderRadius: '6px',
      background: theme.inputBg,
      color: theme.text,
      outline: 'none',
      marginBottom: '8px',
      boxSizing: 'border-box' as const,
    },
    confirmActions: {
      display: 'flex',
      gap: '8px',
    },
    confirmBtn: {
      padding: '6px 16px',
      fontSize: '12px',
      fontWeight: 600,
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      background: '#3b82f6',
      color: '#fff',
    },
    cancelBtn: {
      padding: '6px 16px',
      fontSize: '12px',
      fontWeight: 500,
      border: `1px solid ${theme.border}`,
      borderRadius: '6px',
      cursor: 'pointer',
      background: 'transparent',
      color: theme.textSecondary,
    },
  };

  const getButtonStyle = (value: string) => {
    if (value === 'Active') return styles.successBtn;
    if (value === 'Disabled' || value === 'Locked') return styles.dangerBtn;
    return styles.actionBtn;
  };

  // One line of the Update Items confirmation summary. Unchanged items are omitted.
  const renderItemSummaryRow = (label: string, oldVal: number, newVal: number, delta: number) => {
    if (delta === 0) return null;
    const accent = delta > 0 ? '#22c55e' : '#ef4444';
    return (
      <div key={label} style={styles.summaryRow}>
        <span style={styles.summaryLabel}>{label}</span>
        <span style={styles.summaryValue}>
          {oldVal} <span style={{ color: theme.textSecondary }}>&rarr;</span> {newVal}
          <span style={{ color: accent, fontWeight: 600, marginLeft: '8px' }}>
            ({delta > 0 ? '+' : '−'}{Math.abs(delta)})
          </span>
        </span>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' as const }}>
          <span style={styles.title}>Player Actions</span>
          <span style={styles.statusBadge}>
            Status: {statusLabels[currentStatus] || 'Unknown'}
          </span>
        </div>

        <div style={styles.actionsRow}>
          {statusActions.map(({ label, value }) => (
            <button
              key={value}
              style={getButtonStyle(value)}
              onClick={() => setShowConfirm(value)}
              disabled={loading}
            >
              {label === 'Active' ? 'Activate' : label}
            </button>
          ))}

          <button
            style={styles.ratingBtn}
            onClick={onAdjustRating}
            disabled={loading}
          >
            Adjust Rating
          </button>

          <div style={styles.toggleRow}>
            <span style={styles.toggleLabel}>Ad-Free</span>
            <div style={styles.toggle} onClick={handleAdFreeToggle}>
              <div style={styles.toggleDot} />
            </div>
          </div>

          <button
            style={styles.grantBtn}
            onClick={openUpdateItems}
            disabled={loading}
          >
            Update Items
          </button>
        </div>
      </div>

      {showConfirm !== null && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmTitle}>
            Change status to &ldquo;{statusLabels[showConfirm]}&rdquo;?
          </div>
          <input
            style={styles.reasonInput}
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div style={styles.confirmActions}>
            <button
              style={styles.confirmBtn}
              onClick={() => handleStatusChange(showConfirm)}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Confirm'}
            </button>
            <button
              style={styles.cancelBtn}
              onClick={() => { setShowConfirm(null); setReason(''); }}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showUpdateItems && !itemsReview && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmTitle}>
            Update Items{header.username ? ` for ${header.username}` : ''}
          </div>
          <div style={styles.grantFieldRow}>
            <span style={styles.fieldLabel}>Boards</span>
            <input
              type="number"
              min="0"
              style={styles.numberInput}
              value={itemsBoards}
              onChange={(e) => setItemsBoards(Math.max(0, parseInt(e.target.value, 10) || 0))}
            />
            <span style={styles.fieldLabel}>Hints</span>
            <input
              type="number"
              min="0"
              style={styles.numberInput}
              value={itemsHints}
              onChange={(e) => setItemsHints(Math.max(0, parseInt(e.target.value, 10) || 0))}
            />
            <span style={styles.fieldLabel}>More Time</span>
            <input
              type="number"
              min="0"
              style={styles.numberInput}
              value={itemsMoreTime}
              onChange={(e) => setItemsMoreTime(Math.max(0, parseInt(e.target.value, 10) || 0))}
            />
          </div>
          <div style={{ ...styles.hintText, marginBottom: '8px' }}>
            Fields show the player&rsquo;s current balances. Edit to the new amount, then review before applying.
          </div>
          <input
            style={styles.reasonInput}
            placeholder="Reason (required)"
            value={itemsReason}
            onChange={(e) => setItemsReason(e.target.value)}
          />
          <div style={styles.confirmActions}>
            <button
              style={styles.confirmBtn}
              onClick={() => setItemsReview(true)}
              disabled={loading || !hasItemChanges || !itemsReason.trim()}
            >
              Review
            </button>
            <button
              style={styles.cancelBtn}
              onClick={closeUpdateItems}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showUpdateItems && itemsReview && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmTitle}>
            Confirm changes{header.username ? ` for ${header.username}` : ''}
          </div>
          <div style={{ marginBottom: '10px' }}>
            {renderItemSummaryRow('Boards', currentBoards, itemsBoards, deltaBoards)}
            {renderItemSummaryRow('Hints', currentHints, itemsHints, deltaHints)}
            {renderItemSummaryRow('More Time', currentMoreTime, itemsMoreTime, deltaMoreTime)}
          </div>
          <div style={{ ...styles.hintText, marginBottom: '10px' }}>
            Reason: {itemsReason.trim()}
          </div>
          <div style={styles.confirmActions}>
            <button
              style={styles.confirmBtn}
              onClick={handleUpdateItems}
              disabled={loading}
            >
              {loading ? 'Applying...' : 'Confirm'}
            </button>
            <button
              style={styles.cancelBtn}
              onClick={() => setItemsReview(false)}
              disabled={loading}
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerActions;
